import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFireStorage } from '@angular/fire/storage';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';
import { LoaderService } from '@services/support/loader.service';
import { ModalService } from '@services/support/modal.service';
import { Subscription } from 'rxjs';

import { create as contentDisposition } from "content-disposition-header";

@Component({
  selector: 'app-force-update',
  templateUrl: './force-update.component.html',
  styleUrls: ['./force-update.component.scss']
})
export class ForceUpdateComponent implements OnInit, OnDestroy {

  @ViewChild("environmentTemplate", { static: true }) private environmentTemplate: TemplateRef<any>;
  @ViewChild("replaceFileTemplate", { static: true }) private replaceFileTemplate: TemplateRef<any>;
  @ViewChild("deleteEnvTemplate", { static: true }) private deleteEnvTemplate: TemplateRef<any>;
  @ViewChild("uploadTemplate", { static: true }) private uploadTemplate: TemplateRef<any>;

  environmentList = []
  environmentListSub: Subscription = null

  showInputs: any = {}

  constructor(
    private afdb: AngularFireDatabase,
    private afst: AngularFireStorage,
    private loaderService: LoaderService,
    private modalService: ModalService,
    private flashMessages: FlashMessagesService
  ) { }

  ngOnInit() {
    this.environmentListSub = this.afdb.object<any>("force_update/environments").valueChanges().subscribe(data => {
      const envIds = data ? Object.keys(data) : []
      this.environmentList = envIds.map(eid => {
        const env = data[eid]
        env.id = eid
        return env
      })
    })
  }

  ngOnDestroy() {
    if (this.environmentListSub) { this.environmentListSub.unsubscribe() }
  }

  addEnv() {
    const envId = this.afdb.createPushId()
    const fileId = this.afdb.createPushId()

    const dataModel = {
      env: {
        identifier: "",
        currentVersion: "",
        currentBuild: "",
        minimumVersion: "",
        minimumBuild: "",
        marketUrl: "",
        filename: null,
        file: null,
      },
      apk: false
    }

    const modalId = this.modalService.show({
      template: this.environmentTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          fileChange: (event) => {
            const file: File = event.target.files[0]
            if (file) {
              dataModel.env.filename = file.name
              dataModel.env.file = file
            }
          },
          onEnvFileNameClick: (envFile) => {
            if (!dataModel.env.file) {
              envFile.click()
            }
          },
          deselectFile: (envFile) => {
            dataModel.env.filename = ""
            dataModel.env.file = null
            envFile.value = ''
          },
          apkChange: (envFile) => {
            if (dataModel.apk) {
              dataModel.env.marketUrl = ""
            } else {
              dataModel.env.filename = ""
              dataModel.env.file = null
              envFile.value = ''
            }
          },
          add: () => {
            if (this.environmentList.findIndex(env => env.identifier === dataModel.env.identifier) > -1) {
              this.flashMessages.show('Identifier already in use', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              return
            }
            if (dataModel.env.file) {
              const filePath = `force_update/${envId}/${fileId}`
              this.uploadFile(filePath, dataModel.env.filename, dataModel.env.file)
                .then(() => this.afst.ref(filePath).getDownloadURL().toPromise())
                .then(url => this.afdb.object(`force_update/environments/${envId}`).set({
                  identifier: dataModel.env.identifier,
                  currentVersion: dataModel.env.currentVersion,
                  currentBuild: dataModel.env.currentBuild,
                  minimumVersion: dataModel.env.minimumVersion,
                  minimumBuild: dataModel.env.minimumBuild,
                  filename: dataModel.env.filename,
                  filePath: filePath,
                  fileUrl: url
                }))
                .then(() => this.modalService.hide(modalId))
                .catch(err => {
                  console.log(err)
                  this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
                })
            } else {
              this.afdb.object(`force_update/environments/${envId}`).set({
                identifier: dataModel.env.identifier,
                currentVersion: dataModel.env.currentVersion,
                currentBuild: dataModel.env.currentBuild,
                minimumVersion: dataModel.env.minimumVersion,
                minimumBuild: dataModel.env.minimumBuild,
                marketUrl: dataModel.env.marketUrl
              })
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
            }
          }
        }
      }
    });
  }

  updateCurrentVersion(env, currentVersion: string) {
    if (currentVersion.length === 0) {
      return
    }
    this.afdb.object(`force_update/environments/${env.id}`).update({ currentVersion: currentVersion })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateCurrentBuild(env, currentBuild: string) {
    if (currentBuild.length === 0) {
      return
    }
    this.afdb.object(`force_update/environments/${env.id}`).update({ currentBuild: currentBuild })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateMinimumVersion(env, minimumVersion: string) {
    if (minimumVersion.length === 0) {
      return
    }
    this.afdb.object(`force_update/environments/${env.id}`).update({ minimumVersion: minimumVersion })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateMinimumBuild(env, minimumBuild: string) {
    if (minimumBuild.length === 0) {
      return
    }
    this.afdb.object(`force_update/environments/${env.id}`).update({ minimumBuild: minimumBuild })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateMarketUrl(env, marketUrl: string) {
    if (marketUrl.length === 0) {
      return
    }
    this.afdb.object(`force_update/environments/${env.id}`).update({ marketUrl: marketUrl })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  replaceFile(env) {
    const fileId = this.afdb.createPushId()

    const dataModel = {
      filename: null,
      file: null
    }

    const modalId = this.modalService.show({
      template: this.replaceFileTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          fileChange: (event) => {
            const file: File = event.target.files[0]
            if (file) {
              dataModel.filename = file.name
              dataModel.file = file
            }
          },
          onEnvFileNameClick: (envFile) => {
            if (!dataModel.file) {
              envFile.click()
            }
          },
          deselectFile: (envFile) => {
            dataModel.filename = ""
            dataModel.file = null
            envFile.value = ''
          },
          replace: () => {
            const filePath = `force_update/${env.id}/${fileId}`

            // Upload new file and get download url first
            this.uploadFile(filePath, dataModel.filename, dataModel.file)
              .then(() => this.afst.ref(filePath).getDownloadURL().toPromise())
              .then(url => this.afdb.object(`force_update/environments/${env.id}`).update({
                filename: dataModel.filename,
                filePath: filePath,
                fileUrl: url
              }))
              // then try to remove the old file
              .then(() => {
                this.afst.ref(env.filePath).delete().toPromise()
                  .catch(err => console.log(err))
              })
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Replace failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          }
        }
      }
    });
  }

  deleteEnv(env) {
    const modalId = this.modalService.show({
      template: this.deleteEnvTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          delete: () => {
            this.loaderService.show()
            let promise
            if (env.filePath) {
              promise = this.afst.ref(env.filePath).delete().toPromise()
                .then(() => this.afdb.object(`force_update/environments/${env.id}`).remove())
            } else {
              promise = this.afdb.object(`force_update/environments/${env.id}`).remove()
            }
            promise
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Delete Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
              .finally(() => {
                this.loaderService.hide()
                this.modalService.hide(modalId)
              })
          }
        }
      }
    })
  }

  uploadFile(filePath: string, filename: string, file: File) {
    const uploadTask = this.afst.ref(filePath).put(file, { contentDisposition: contentDisposition(filename), contentType: file.type })

    const dataModel = { progress: 0 }
    const modalId = this.modalService.show({
      template: this.uploadTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          cancel: () => {
            uploadTask.cancel()
            this.modalService.hide(modalId)
          }
        }
      }
    })
    uploadTask.percentageChanges().subscribe(progress => { dataModel.progress = progress })
    return uploadTask
      .then(() => new Promise<void>(resolve => setTimeout(() => resolve(), 1000)))
      .then(() => this.modalService.hide(modalId))
      .catch(e => {
        this.modalService.hide(modalId)
        throw e
      })
  }
}
